import { type Option } from '@/components/Form';
import { useState } from 'react';
import {
  type FilterFormState,
  type OrganizationSearchCondition,
  type MultiSelectFieldKey,
  type OrganizationSort,
} from '../types';
import { SORT_DIRECTION } from '@/constants/sort';

const INITIAL_FORM_STATE: FilterFormState = {
  query: '',
  types: [],
  statuses: [],
  countries: [],
  countryTags: [],
  sort: { field: 'NAME', direction: SORT_DIRECTION.ASC },
};

const normalizeQuery = (query: string): string | undefined => {
  const trimmedQuery = query.trim();
  return trimmedQuery.length > 0 ? trimmedQuery : undefined;
};

const toConditionValues = (options: Option[]): string[] | undefined =>
  options.length > 0 ? options.map(({ value }) => value) : undefined;

export const useOrganizationFilters = () => {
  const [appliedFilters, setAppliedFilters] =
    useState<FilterFormState>(INITIAL_FORM_STATE);
  const [queryInput, setQueryInput] = useState(INITIAL_FORM_STATE.query);
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const formState: FilterFormState = { ...appliedFilters, query: queryInput };

  const condition: OrganizationSearchCondition = {
    query: normalizeQuery(appliedFilters.query),
    types: toConditionValues(appliedFilters.types),
    statuses: toConditionValues(appliedFilters.statuses),
    countries: toConditionValues(appliedFilters.countries),
    countryTags: toConditionValues(appliedFilters.countryTags),
    sort: appliedFilters.sort,
  };

  const handleSelectChange =
    (field: MultiSelectFieldKey) => (options: readonly Option[] | null) => {
      const nextOptions = options ? [...options] : [];
      setAppliedFilters((prev) => ({
        ...prev,
        [field]: nextOptions,
      }));
    };

  const handleSubmit = (submittedQuery: string | undefined) => {
    setAppliedFilters((prev) => ({
      ...prev,
      query: submittedQuery ?? '',
    }));
  };

  const handleReset = () => {
    setAppliedFilters(INITIAL_FORM_STATE);
    setQueryInput(INITIAL_FORM_STATE.query);
  };

  const handleSortChange = (sort: OrganizationSort) => {
    setAppliedFilters((prev) => ({ ...prev, sort }));
  };

  return {
    formState,
    appliedFilters,
    isFilterExpanded,
    condition,
    setQueryInput,
    setIsFilterExpanded,
    handleSubmit,
    handleSelectChange,
    handleReset,
    handleSortChange,
  };
};
